import { BACKEND_URL } from "@/lib/backendUrl";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Award, ShieldCheck } from "lucide-react";

const API_BASE = `${BACKEND_URL}/api`;
const pretty = (s) => String(s || "").slice(0, 16).replace("T", " ");
const words = (s) => String(s || "").replace(/_/g, " ");

export default function PublicResults() {
  const { electionRef } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/public/election-results/${electionRef}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("No declared result yet"))))
      .then(setData)
      .catch((e) => setError(e.message));
  }, [electionRef]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0f14] px-6">
        <p className="text-[13px] text-neutral-400" data-testid="public-results-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0f14]">
        <p className="text-[13px] text-neutral-500">Loading the declared result…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0f14] px-6 py-16 text-neutral-200">
      <div className="mx-auto max-w-3xl space-y-10">
        <header className="space-y-3">
          <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-400/80">
            Declared result · published {pretty(data.declared_at)}
          </p>
          <h1 className="text-4xl font-semibold leading-tight text-white sm:text-5xl"
              data-testid="public-election-name">
            {data.election_name}
          </h1>
          <p className="text-base text-neutral-400">
            Term {data.term}. Turnout{" "}
            <span className="font-mono text-white" data-testid="public-turnout">
              {data.turnout.percent}%
            </span>{" "}
            — {data.turnout.cast} of {data.turnout.roll} on the roll. Least turnout required{" "}
            {data.turnout.minimum_percent}%, {data.turnout.met ? "met" : "not met"}; quorum{" "}
            {data.turnout.quorum}, {data.turnout.quorum_met ? "met" : "not met"}.
          </p>
        </header>

        <div className="space-y-6">
          {data.sections.map((s) => (
            <section key={s.section_ref} data-testid={`public-section-${s.section_ref}`}
                     className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-lg font-medium text-white">{s.title}</h2>
                <span className={`rounded-full border px-2.5 py-0.5 text-[10.5px] uppercase tracking-wider ${
                  s.outcome === "tie" ? "border-amber-400/40 text-amber-300"
                    : "border-emerald-400/40 text-emerald-300"}`}
                      data-testid={`public-outcome-${s.section_ref}`}>
                  {words(s.outcome)}
                </span>
              </div>
              <p className="mt-1 text-[11px] uppercase tracking-wider text-neutral-500">
                {s.method_label} · {s.seats} seat(s)
              </p>
              <p className="mt-1 text-[12px] text-neutral-500">
                {s.valid} valid · {s.abstain} abstained · {s.blank} blank · {s.spoilt} spoilt
                {s.quota ? ` · quota ${s.quota}` : ""}
              </p>
              <div className="mt-4 space-y-2">
                {s.tallies.map((t) => (
                  <div key={t.option_ref} className="flex items-center gap-3"
                       data-testid={`public-tally-${t.option_ref}`}>
                    <span className="w-56 shrink-0 text-[13px]">
                      {t.elected && <Award className="mr-1.5 inline h-3.5 w-3.5 text-emerald-400" />}
                      <span className={t.elected ? "text-white" : "text-neutral-400"}>
                        {t.label}
                      </span>
                    </span>
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
                      <div className={`h-full rounded-full transition-[width] duration-700 ${
                        t.elected ? "bg-emerald-400" : t.tied ? "bg-amber-400" : "bg-neutral-600"}`}
                           style={{ width: `${s.valid
                             ? Math.min(100, (t.votes / Math.max(1, s.valid)) * 100) : 0}%` }} />
                    </div>
                    <span className="w-14 shrink-0 text-right font-mono text-[12px] text-neutral-400">
                      {t.votes}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        <section className="space-y-3" data-testid="public-share">
          <img alt="Result card" src={`${API_BASE}/public/election-results/${electionRef}/card.png`}
               data-testid="public-card"
               className="w-full rounded-2xl border border-white/10" />
          <div className="flex flex-wrap gap-3 text-[12px]">
            <a data-testid="public-download-card" className="text-emerald-300 hover:underline"
               href={`${API_BASE}/public/election-results/${electionRef}/card.png`}
               download target="_blank" rel="noreferrer">
              Download this card
            </a>
            <button type="button" data-testid="public-copy-link"
                    className="text-emerald-300 hover:underline"
                    onClick={() => {
                      navigator.clipboard?.writeText(
                        `${API_BASE}/public/election-results/${electionRef}/share`);
                      setCopied(true);
                    }}>
              {copied ? "Link copied" : "Copy a shareable link"}
            </button>
          </div>
        </section>

        <footer className="space-y-2 border-t border-white/10 pt-6 text-[12px] text-neutral-500">
          <p data-testid="public-certification">
            <ShieldCheck className="mr-1.5 inline h-3.5 w-3.5 text-emerald-400" />
            Counted by {data.counted_by}, certified by {data.certified_by} on{" "}
            {pretty(data.certified_at)} and declared by {data.declared_by}. Election rules v
            {data.rules_version}.
          </p>
          {data.certification_minute && <p>{data.certification_minute}</p>}
          {data.override_reason && <p>Certified despite a failed test: {data.override_reason}</p>}
          {(data.lots || []).map((lot) => (
            <p key={lot.lot_ref}>
              {lot.section_title}: tied between {lot.between.join(" and ")} and decided by lot in
              favour of {lot.won_by}.
            </p>
          ))}
          {(data.recounts || []).map((rc) => (
            <p key={rc.recount_ref}>
              Recount {rc.recount_ref}: {rc.unchanged ? "no change to the result"
                : `the outcome changed in ${rc.changed_sections.length} section(s)`}.
            </p>
          ))}
          <p>
            Election Commission: {data.commission.map((m) => m.name).join(", ")}. A candidate could
            ask for a recount until {pretty(data.recount_window?.closes_at)}.
          </p>
        </footer>
      </div>
    </div>
  );
}
